/**
 * Runner event shapes and the concrete readers every client needs.
 *
 * The runner's transcript is a sequence of JSON events. This module names
 * them, reads the fields applications render, and answers the questions a
 * reconnecting client asks of a transcript: where to resume from, whether a
 * turn is still open, which input sets a turn published.
 */
export const INPUT_ID_RE = /^[a-f0-9]{16}$/;
export const SESSION_ID_RE = /^[A-Za-z0-9_.:-]+$/;
/** One `input://<request_id>` anchor, wherever the model placed it in the text. */
export const INPUT_ANCHOR_RE = /input:\/\/([a-f0-9]{16})/;
export function isObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
export function eventType(event) {
    if (!isObject(event))
        return null;
    return typeof event.type === 'string' ? event.type : null;
}
export function eventUuid(event) {
    if (!isObject(event))
        return null;
    return typeof event.uuid === 'string' && event.uuid ? event.uuid : null;
}
const TRANSCRIPT_TYPES = new Set([
    'init',
    'user',
    'assistant',
    'assistant_delta',
    'tool_started',
    'progress',
    'error',
    'end',
]);
/** Events the durable transcript carries; `connect` and keepalives are not. */
export function isTranscriptEvent(event) {
    const kind = eventType(event);
    return kind !== null && TRANSCRIPT_TYPES.has(kind);
}
/**
 * The `user` events a harness emits to carry tool results: every block is a
 * `tool_result`. They are not a human message and never render as one.
 */
export function isToolResultEvent(event) {
    if (eventType(event) !== 'user')
        return false;
    const blocks = contentBlocks(event.message);
    return blocks.length > 0 && blocks.every((block) => block.type === 'tool_result');
}
/** The event timestamp as epoch milliseconds, or undefined when it has none. */
export function eventTimestamp(event) {
    if (!isObject(event))
        return undefined;
    const stamp = event.timestamp;
    if (typeof stamp === 'number' && Number.isFinite(stamp))
        return stamp;
    if (typeof stamp === 'string' && stamp) {
        const parsed = /^\d+$/.test(stamp) ? Number(stamp) : Date.parse(stamp);
        return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
}
/** `success`, `failed` or `cancelled`; old transcripts say `completed`. */
export function endStatus(event) {
    const status = event.status;
    if (status === 'completed' || status === 'success')
        return 'success';
    if (status === 'cancelled')
        return 'cancelled';
    return 'failed';
}
export function contentBlocks(message) {
    if (!isObject(message))
        return [];
    const content = message.content;
    if (typeof content === 'string')
        return content ? [{ type: 'text', text: content }] : [];
    if (!Array.isArray(content))
        return [];
    return content.filter((block) => isObject(block) && typeof block.type === 'string');
}
/** The text of a message, whether its content is a string or blocks. */
export function messageText(message) {
    return contentBlocks(message)
        .filter((block) => block.type === 'text' && typeof block.text === 'string')
        .map((block) => block.text)
        .join('');
}
export function messageAttachments(message) {
    const found = [];
    for (const block of contentBlocks(message)) {
        if (block.type !== 'attachment')
            continue;
        const raw = block;
        if (typeof raw.id !== 'string' || typeof raw.filename !== 'string')
            continue;
        found.push({
            id: raw.id,
            filename: raw.filename,
            contentType: typeof raw.content_type === 'string' ? raw.content_type : undefined,
            size: typeof raw.size === 'number' ? raw.size : undefined,
        });
    }
    return found;
}
export function inputForm(block) {
    if (!isObject(block) || block.type !== 'form')
        return null;
    const requestId = block.request_id;
    const setId = block.set_id;
    const kind = block.kind;
    if (typeof requestId !== 'string' || !INPUT_ID_RE.test(requestId))
        return null;
    if (typeof setId !== 'string' || !INPUT_ID_RE.test(setId))
        return null;
    if (kind !== 'question' && kind !== 'form' && kind !== 'approval' && kind !== 'url')
        return null;
    return {
        requestId,
        setId,
        kind,
        title: typeof block.title === 'string' ? block.title : undefined,
        prompt: typeof block.prompt === 'string' ? block.prompt : undefined,
        url: typeof block.url === 'string' ? block.url : undefined,
        schema: isObject(block.schema) ? block.schema : {},
    };
}
export function messageForms(message) {
    const forms = [];
    for (const block of contentBlocks(message)) {
        const form = inputForm(block);
        if (form)
            forms.push(form);
    }
    return forms;
}
/** A form as an input record describes it, for pages that render a set. */
export function recordForm(record) {
    return inputForm({ ...record, type: 'form' });
}
/**
 * Split assistant text on its `input://` anchors so a form renders where
 * the agent placed it, on its own line or inside one. Anchors without a
 * matching form disappear; forms without an anchor come after the text.
 */
export function assistantSegments(text, forms) {
    const byId = new Map(forms.map((form) => [form.requestId, form]));
    const placed = new Set();
    const segments = [];
    const pushText = (chunk) => {
        const trimmed = chunk.trim();
        if (trimmed)
            segments.push({ kind: 'text', text: trimmed });
    };
    const anchors = new RegExp(INPUT_ANCHOR_RE.source, 'g');
    let cursor = 0;
    for (const match of text.matchAll(anchors)) {
        const form = byId.get(match[1]);
        if (!form || placed.has(form.requestId))
            continue;
        pushText(text.slice(cursor, match.index));
        cursor = match.index + match[0].length;
        placed.add(form.requestId);
        segments.push({ kind: 'form', form });
    }
    pushText(text.slice(cursor));
    for (const form of forms) {
        if (!placed.has(form.requestId))
            segments.push({ kind: 'form', form });
    }
    return segments;
}
/**
 * The text without its anchors, for a surface that renders forms elsewhere.
 * An inline anchor leaves one space between its neighbours; an anchor on
 * its own line leaves no blank line behind.
 */
export function stripInputAnchors(text) {
    return text
        .replace(new RegExp(`[ \\t]*${INPUT_ANCHOR_RE.source}[ \\t]*`, 'g'), ' ')
        .split('\n')
        .map((line) => line.trim())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
/** The set id an `end` event published, if the turn asked for input. */
export function publishedInputSet(event) {
    if (eventType(event) !== 'end')
        return null;
    const setId = event.input_set_id;
    return typeof setId === 'string' && INPUT_ID_RE.test(setId) ? setId : null;
}
export function publishedInputSetIds(events) {
    const found = [];
    for (const event of events) {
        const setId = publishedInputSet(event);
        if (setId && !found.includes(setId))
            found.push(setId);
    }
    return found;
}
/** Sets to freeze before the next turn: published and not yet frozen. */
export function openInputSetIds(published, frozen) {
    const closed = new Set(frozen);
    const open = [];
    for (const setId of published) {
        if (!closed.has(setId) && !open.includes(setId))
            open.push(setId);
    }
    return open;
}
/** True for a message event the client made up itself (`optimisticUserEvent`). */
function isPendingEvent(event) {
    return isObject(event) && isObject(event.message) && event.message.pending === true;
}
/**
 * The uuid to resume after: the last user or assistant event's. A pending
 * message the client made up has a placeholder uuid the runner never saw,
 * so it does not move the cursor.
 */
export function replayCursor(events) {
    let cursor = null;
    for (const event of events)
        cursor = resumeCursorAfter(cursor, event);
    return cursor;
}
export function resumeCursorAfter(previous, event) {
    const kind = eventType(event);
    if ((kind === 'user' || kind === 'assistant') && !isPendingEvent(event))
        return eventUuid(event) ?? previous;
    return previous;
}
/** True when the last user message has no `end` after it. */
export function hasUnfinishedTurn(events) {
    let unfinished = false;
    for (const event of events) {
        const kind = eventType(event);
        if (kind === 'user')
            unfinished = true;
        else if (kind === 'end')
            unfinished = false;
    }
    return unfinished;
}
let pendingCounter = 0;
/**
 * The user event a client renders before the runner echoes the message. The
 * runner's own `user` event replaces it; `retract_pending` removes it when
 * the send fails.
 */
export function optimisticUserEvent(prompt, files = [], requestId) {
    pendingCounter += 1;
    const blocks = [];
    if (prompt)
        blocks.push({ type: 'text', text: prompt });
    files.forEach((file, index) => {
        blocks.push({
            type: 'attachment',
            id: `pending-${pendingCounter}-${index}`,
            filename: file.name,
            content_type: file.type,
            size: file.size,
        });
    });
    return {
        type: 'user',
        ...(requestId ? { request_id: requestId } : {}),
        uuid: `pending-${pendingCounter}`,
        message: { content: blocks, pending: true },
        timestamp: Date.now(),
    };
}
